import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type {
  LinkDevicesResponse,
  PeripheralBlock,
  TerminalConfig,
  TerminalPeripheralConfig,
  TerminalPeripheralConfigOverride,
  TerminalShiftBoundaryParamState,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/** The kinds that sell. The terminals list and its filters show only these. */
export type TillKind = 'pos' | 'sco' | 'attendant';
/** RL-449: `link` is a re:form.lite.link device, listed on its own page. */
export type TerminalKind = TillKind | 'link';
export type TerminalStatus = 'provisioned' | 'active' | 'revoked';

export type Terminal = {
  id: string;
  code: string;
  displayName: string;
  kind: TerminalKind;
  profileId: string | null;
  locationId: string | null;
  /** Which register this is inside its own store — GP's `POSNr` (YUKI-197). */
  posNrInStore: number | null;
  /**
   * The integer `POSId` the GP export sends for this till (YUKI-239). Null
   * means the export sends the terminal's code instead.
   */
  gpPosIdAuthored: number | null;
  /**
   * VL-266 — the register's number in the retailer's own back office, carried
   * on receipts exported to Masterkasa. A string, so a leading zero survives:
   * "0475" is a real register in an estate that mixes 3- and 4-digit numbers.
   */
  posExternalNumber: string | null;
  status: TerminalStatus;
  lastHeartbeatAt: string | null;
  lastVersion: string | null;
  activatedAt: string | null;
  isOnline?: boolean;
  profile: TerminalProfile | null;
};

/**
 * Terminal configuration authored in BOS and delivered by sync
 * (VL-135 cash rounding · VL-136 tax groups · VL-171 fiscal binding).
 *
 * These are the till's OWN key names, in snake_case — they come from a parser
 * we do not control, so they are data rather than style. Renaming them to
 * match this codebase's camelCase would produce config that saves, syncs, and
 * does nothing.
 *
 * Runtime validation stays on the server, but the compile-time shape comes
 * from the same contracts package the server and till consume. The exhaustive
 * label/default/editor registries below then make a new contract namespace a
 * build failure until Back Office can author it.
 */
export type TerminalConfigValue = TerminalConfig;
export type TerminalCashRounding = NonNullable<TerminalConfigValue['cash_rounding']>;
export type TerminalTaxGroup = NonNullable<
  NonNullable<TerminalConfigValue['tax_rules']>['tax_groups'][number]
>;
export type TerminalLoyalty = NonNullable<TerminalConfigValue['loyalty']>;
export type TerminalCatalogueScope = NonNullable<TerminalConfigValue['catalogue_scope']>;

/** Config namespaces, in display order. The unit of override. */
export type ConfigNamespace = Extract<keyof TerminalConfigValue, string>;
export const NAMESPACE_LABELS = {
  cash_rounding: 'Cash rounding',
  tax_rules: 'Tax groups',
  fiscal: 'Fiscal binding',
  loyalty: 'Loyalty link',
  catalogue_scope: 'Catalogue scope',
} satisfies Record<ConfigNamespace, string>;
export const CONFIG_NAMESPACES = Object.keys(NAMESPACE_LABELS) as ConfigNamespace[];

/**
 * A per-terminal override. `null` for a namespace is a TOMBSTONE — "ignore the
 * profile and use my local default" — which is a different instruction from
 * the namespace being absent, and the UI must keep the two distinguishable.
 */
export type TerminalConfigOverrideValue = {
  [K in ConfigNamespace]?: NonNullable<TerminalConfigValue[K]> | null;
};

export type TerminalConfigView = {
  profile: { id: string; name: string } | null;
  profileConfig: TerminalConfigValue;
  override: TerminalConfigOverrideValue;
  effective: TerminalConfigValue;
  configVersion: number;
};

export type TerminalProfile = {
  id: string;
  name: string;
  description: string | null;
  primaryLocale: string;
  availableLocales: string[];
  /** Peripheral flags (RFL-177), e.g. 'scale' for a produce scale. */
  peripherals: string[];
  /** ADR-0012 §3 / FN-96 — executable when the kind alone does not decide. */
  applicationVariant: 'food_self_order' | 'kitchen_station' | null;
  countryPackId: string | null;
  config: TerminalConfigValue | null;
  configVersion: number;
  /**
   * RFL-238 group hardware defaults. Its own version token — hardware is
   * authored by whoever is next to the printer, tax rules by someone else, and
   * one shared counter would make their saves collide.
   */
  peripheralConfig: TerminalPeripheralConfig | null;
  peripheralConfigVersion: number;
};

export type RegisterTerminalResult = {
  terminal: Terminal;
  activationCode: string;
  expiresAt: string;
};

export type ListTerminalsResponse = {
  terminals: Terminal[];
  page: number;
  pageSize: number;
  total: number;
};

const TERMINALS_KEY = ['terminal-management', 'terminals'] as const;
const PROFILES_KEY = ['terminal-management', 'profiles'] as const;
const SYNC_LAG_KEY = ['terminal-management', 'sync-lag'] as const;
const TERMINAL_CONFIG_KEY = (id: string) => ['terminal-management', 'config', id] as const;

export type SyncLagLevel = 'green' | 'yellow' | 'red' | 'unknown';
export type SyncLagDomainState = {
  domain: string;
  lastSuccessAt: string | null;
  lagSeconds: number | null;
  level: SyncLagLevel;
};
export type SyncLagSnapshot = {
  terminalId: string;
  reportedAt: string | null;
  domains: SyncLagDomainState[];
  worstLevel: SyncLagLevel;
  computedAt: string;
  /** Kitchen station only (FN-92): broker / printer alert state. */
  kitchen?: {
    level: SyncLagLevel;
    reason: string | null;
    activeOrders: number | null;
    broker: {
      connected: boolean;
      consumerAlive: boolean;
      outboxDepth: number;
      lastMessageAt: string | null;
    };
    printer: { configured: boolean; ready: boolean };
  };
  /**
   * RL-267: shift-boundary parameters as the till last reported them — the
   * value it operates under and any value held for the next shift boundary.
   * Absent when the till never reported them.
   */
  shiftBoundaryParams?: TerminalShiftBoundaryParamState[];
  /**
   * SSC-260: the SecureScale weight copy as the lane last reported it. Absent
   * when the lane never reported one. Information for staff; never graded.
   */
  secureScaleCopy?: SecureScaleCopyFacts;
};

/** Who asserts a fact: central's signed replica bundle, or the lane's own clock. */
export type SecureScaleCopyVersionFact = {
  versionId: string;
  revision: number;
  createdAt: string | null;
  source: 'replica_bundle';
};
export type SecureScaleCopyFacts = {
  checking: (SecureScaleCopyVersionFact & { basis: 'pinned' | 'active' }) | null;
  pending: SecureScaleCopyVersionFact | null;
  lastCompleteSync: { at: string | null; source: 'lane_clock' };
};

/**
 * Bulk sync-lag snapshot for the terminals list. Refetches on the
 * same 30-second cadence as the terminals query so the dots stay
 * fresh without manual reloads.
 */
export function useSyncLagBulk() {
  return useQuery({
    queryKey: SYNC_LAG_KEY,
    queryFn: async (): Promise<{ snapshots: SyncLagSnapshot[] }> => {
      const res = await apiClient.get<{ snapshots: SyncLagSnapshot[] }>('/terminals-sync-lag');
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

export function useTerminals(status?: TerminalStatus, online?: boolean, kind?: TerminalKind) {
  return useQuery({
    queryKey: [
      ...TERMINALS_KEY,
      status ?? 'any',
      online ?? 'any-connectivity',
      kind ?? 'any-kind',
    ] as const,
    queryFn: async (): Promise<ListTerminalsResponse> => {
      const res = await apiClient.get<ListTerminalsResponse>('/terminals', {
        params: {
          ...(status === undefined ? {} : { status }),
          ...(online === undefined ? {} : { online }),
          ...(kind === undefined ? {} : { kind }),
        },
      });
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

/**
 * Every non-revoked terminal, for pickers that need the whole estate rather
 * than a page of it (the parameter editor's scope selector). The API caps one
 * page at 200, so consume every page rather than silently hiding terminals in
 * larger estates.
 */
export function useAllTerminals(enabled = true) {
  return useQuery({
    queryKey: [...TERMINALS_KEY, 'all'] as const,
    queryFn: async (): Promise<Terminal[]> => {
      const terminals: Terminal[] = [];
      let page = 1;
      while (true) {
        const res = await apiClient.get<ListTerminalsResponse>('/terminals', {
          params: { page, pageSize: 200 },
        });
        terminals.push(...res.data.terminals);
        if (page * res.data.pageSize >= res.data.total) break;
        page += 1;
      }
      return terminals.filter((terminal) => terminal.status !== 'revoked');
    },
    enabled,
  });
}

export function useRegisterTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      code: string;
      displayName: string;
      kind: TerminalKind;
      profileId?: string;
      locationId?: string;
      posExternalNumber?: string;
    }): Promise<RegisterTerminalResult> => {
      const res = await apiClient.post<RegisterTerminalResult>('/terminals', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TERMINALS_KEY }),
  });
}

export function useUpdateTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: {
        displayName?: string;
        profileId?: string | null;
        locationId?: string | null;
        posNrInStore?: number | null;
        gpPosIdAuthored?: number | null;
        posExternalNumber?: string | null;
      } & (
        | {
            configOverride: TerminalConfigOverrideValue | null;
            expectedConfigVersion: number;
          }
        | { configOverride?: undefined; expectedConfigVersion?: never }
      );
    }): Promise<Terminal> => {
      const res = await apiClient.patch<Terminal>(`/terminals/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: async (_data, vars) => {
      // Keep mutateAsync pending until the version-bearing read model is fresh.
      // Otherwise a rapid second edit starts from the just-invalidated stale
      // bag and immediately conflicts (or, before config versions, overwrote).
      await Promise.all([
        qc.invalidateQueries({ queryKey: TERMINALS_KEY }),
        qc.invalidateQueries({ queryKey: TERMINAL_CONFIG_KEY(vars.id) }),
      ]);
    },
  });
}

/**
 * Profile config, this terminal's override, and the merged result the till
 * will actually receive (RFL-241). The merge is done server-side with the same
 * function the sync feed uses — see the endpoint's comment for why that
 * matters more than it looks.
 */
export function useTerminalConfig(id: string | undefined) {
  return useQuery({
    queryKey: TERMINAL_CONFIG_KEY(id ?? ''),
    enabled: id !== undefined,
    queryFn: async (): Promise<TerminalConfigView> => {
      const res = await apiClient.get<TerminalConfigView>(`/terminals/${id}/config`);
      return res.data;
    },
  });
}

/**
 * Replace or clear a profile's whole config bag.
 *
 * Whole-bag replacement, not a patch, because that is what the endpoint does:
 * sending one namespace would silently drop the others.
 */
export function useUpdateProfileConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      config: TerminalConfigValue | null;
      expectedConfigVersion: number;
    }): Promise<TerminalProfile> => {
      const res = await apiClient.patch<TerminalProfile>(`/terminal-profiles/${vars.id}/config`, {
        config: vars.config,
        expectedConfigVersion: vars.expectedConfigVersion,
      });
      return res.data;
    },
    onSuccess: async () => {
      // Every terminal on this profile now resolves differently.
      await Promise.all([
        qc.invalidateQueries({ queryKey: PROFILES_KEY }),
        qc.invalidateQueries({ queryKey: ['terminal-management', 'config'] }),
      ]);
    },
  });
}

export function useRevokeTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/terminals/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TERMINALS_KEY }),
  });
}

/**
 * RL-449 — Link devices with their store and connector-source summaries.
 * Keyed under the terminals key so registering, revoking or re-issuing a code
 * refreshes this list too.
 */
export function useLinkDevices(status?: TerminalStatus) {
  return useQuery({
    queryKey: [...TERMINALS_KEY, 'link-devices', status ?? 'any'] as const,
    queryFn: async (): Promise<LinkDevicesResponse> => {
      const res = await apiClient.get<LinkDevicesResponse>('/connectors/link/devices', {
        params: status === undefined ? {} : { status },
      });
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

export function useRegenerateActivationCode() {
  return useMutation({
    mutationFn: async (id: string): Promise<{ activationCode: string; expiresAt: string }> => {
      const res = await apiClient.post(`/terminals/${id}/regenerate-code`);
      return res.data as { activationCode: string; expiresAt: string };
    },
  });
}

// ---------- profiles ----------

export function useTerminalProfiles(enabled = true) {
  return useQuery({
    queryKey: PROFILES_KEY,
    queryFn: async (): Promise<TerminalProfile[]> => {
      const res = await apiClient.get<TerminalProfile[]>('/terminal-profiles');
      return res.data;
    },
    enabled,
  });
}

export function useCreateTerminalProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      primaryLocale: string;
      availableLocales: string[];
      peripherals?: string[];
      applicationVariant?: 'food_self_order' | 'kitchen_station' | null;
    }): Promise<TerminalProfile> => {
      const res = await apiClient.post<TerminalProfile>('/terminal-profiles', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES_KEY }),
  });
}

// ---------- RFL-240 / RFL-241 · peripheral (hardware) configuration ----------

export type PeripheralBlockOrigin = 'group' | 'terminal' | 'cleared' | 'unset';

export type TerminalPeripheralConfigView = {
  profile: { id: string; name: string } | null;
  profileConfig: TerminalPeripheralConfig;
  override: TerminalPeripheralConfigOverride;
  effective: TerminalPeripheralConfig;
  /** Which level decided each block. Computed server-side — see below. */
  origins: Record<PeripheralBlock, PeripheralBlockOrigin>;
  peripheralConfigVersion: number;
};

export const PERIPHERAL_BLOCK_LABELS: Record<PeripheralBlock, string> = {
  receiptPrinter: 'Receipt printer',
  barcodeScanner: 'Barcode scanner',
  pinPad: 'PIN pad (legacy network block)',
  fiscalSecurityModule: 'Fiscal security module (legacy serial block)',
  fiscalSecurityModuleEndpoint: 'Fiscal security module endpoint',
  fiscalSecurityModuleWorkDirectory: 'Fiscal security module work directory',
  fiscalSidecar: 'Fiscal sidecar (legacy network block)',
  paymentTerminal: 'Payment terminal',
  secondPaymentTerminal: 'Second payment terminal (Oschadbank)',
  fiscalService: 'Fiscal service',
  baggingScale: 'Bagging scale (SCO)',
  attendantCall: 'Attendant call (SCO)',
  authenticationReader: 'Authentication reader (Attendant)',
  lanService: 'LAN service (Attendant)',
  labelPrinter: 'Label printer (Kitchen station)',
  softFiscalAndroid: 'SoftFiscal Android (LT security module)',
};

/**
 * Roles that have a cold-start consumer in a shipped terminal app today.
 * Keep parse-only future roles out of authoring: showing a field that syncs
 * successfully but is never opened by an app is worse than not exposing it.
 */
export const AUTHORABLE_PERIPHERAL_BLOCKS: readonly PeripheralBlock[] = [
  'receiptPrinter',
  'barcodeScanner',
  'pinPad',
  'fiscalSecurityModule',
  'fiscalSecurityModuleEndpoint',
  // MICRO-56: the micromarket lane reads its SoftFiscal journal from here.
  'fiscalSecurityModuleWorkDirectory',
  'fiscalSidecar',
  'paymentTerminal',
  'secondPaymentTerminal',
  'fiscalService',
  // SSC-85/SSC-116: the standard and Varus SCO open their security scale from
  // this block, including the explicit "no scale fitted".
  'baggingScale',
  'lanService',
  'labelPrinter',
  // RL-957: read by Back Office itself, which builds SoftFiscal Android's
  // config file from it (the download, S2c). Tills parse the block once
  // RL-970 lands; until then a till ignores it.
  'softFiscalAndroid',
];

const TERMINAL_PERIPHERAL_KEY = (id: string) => ['terminal-peripheral-config', id] as const;

/**
 * The group defaults, this terminal's override, which level decided each
 * block, and the hardware the till will actually receive (RFL-241).
 *
 * `origins` comes from the server rather than being derived here. The client
 * could almost compute it — but "almost" means a screen that disagrees with
 * the sync feed about whether a port is inherited, and the operator believes
 * the screen.
 */
export function useTerminalPeripheralConfig(id: string | undefined) {
  return useQuery({
    queryKey: TERMINAL_PERIPHERAL_KEY(id ?? ''),
    enabled: id !== undefined,
    queryFn: async (): Promise<TerminalPeripheralConfigView> => {
      const res = await apiClient.get<TerminalPeripheralConfigView>(
        `/terminals/${id}/peripheral-config`,
      );
      return res.data;
    },
  });
}

/** Replace or clear one terminal's hardware override. */
export function useUpdateTerminalPeripheralConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      peripheralConfigOverride: TerminalPeripheralConfigOverride | null;
      expectedPeripheralConfigVersion: number;
    }): Promise<TerminalPeripheralConfigView> => {
      const res = await apiClient.patch<TerminalPeripheralConfigView>(
        `/terminals/${vars.id}/peripheral-config`,
        {
          peripheralConfigOverride: vars.peripheralConfigOverride,
          expectedPeripheralConfigVersion: vars.expectedPeripheralConfigVersion,
        },
      );
      return res.data;
    },
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: TERMINAL_PERIPHERAL_KEY(vars.id) });
    },
  });
}

/**
 * Replace or clear the group-level hardware defaults.
 *
 * Whole-bag replacement like the config equivalent: sending one block would
 * silently drop the others.
 */
export function useUpdateProfilePeripheralConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      peripheralConfig: TerminalPeripheralConfig | null;
      expectedPeripheralConfigVersion: number;
    }): Promise<TerminalProfile> => {
      const res = await apiClient.patch<TerminalProfile>(
        `/terminal-profiles/${vars.id}/peripheral-config`,
        {
          peripheralConfig: vars.peripheralConfig,
          expectedPeripheralConfigVersion: vars.expectedPeripheralConfigVersion,
        },
      );
      return res.data;
    },
    onSuccess: async () => {
      // Every terminal on this profile now resolves different hardware.
      await Promise.all([
        qc.invalidateQueries({ queryKey: PROFILES_KEY }),
        qc.invalidateQueries({ queryKey: ['terminal-peripheral-config'] }),
      ]);
    },
  });
}

/**
 * RL-957 — download one till's SoftFiscal Android config file, built by the
 * server from the till's saved hardware. A block the APK cannot take is
 * refused with what to fix; that message is thrown as the error's message
 * (empty when the server gave none).
 */
export async function downloadSoftFiscalConfig(id: string): Promise<{ filename: string }> {
  let res: { data: Blob; headers: unknown };
  try {
    res = await apiClient.get<Blob>(`/terminals/${id}/softfiscal-config`, {
      responseType: 'blob',
    });
  } catch (err) {
    throw new Error((await softFiscalDownloadMessage(err)) ?? '');
  }
  const disposition = String((res.headers as Record<string, unknown>)['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'softfiscal-config.json';
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return { filename };
}

/**
 * Largest refusal body read for its message. The server's 422 is a few hundred
 * bytes; anything bigger is not that refusal (a proxy page, a mistake) and is
 * not read at all, so the page falls back to its generic message.
 */
export const SOFTFISCAL_REFUSAL_MAX_BYTES = 16 * 1024;
/** Longest server message shown as is; a longer one gets the generic message. */
export const SOFTFISCAL_REFUSAL_MAX_MESSAGE = 1000;

/**
 * The server's message in a refused download. The request asked for a blob,
 * so the error body is a blob too and has to be read as JSON first, and only
 * when it is small (checked on its size before reading).
 */
export async function softFiscalDownloadMessage(err: unknown): Promise<string | null> {
  if (!isAxiosError(err)) return null;
  let body: unknown = err.response?.data;
  if (body instanceof Blob) {
    if (body.size > SOFTFISCAL_REFUSAL_MAX_BYTES) return null;
    try {
      body = JSON.parse(await body.text()) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof body !== 'object' || body === null || !('message' in body)) return null;
  const message = (body as { message: unknown }).message;
  return typeof message === 'string' &&
    message !== '' &&
    message.length <= SOFTFISCAL_REFUSAL_MAX_MESSAGE
    ? message
    : null;
}
